/**
 * Crisis Location Geocoding Service
 * Handles automatic geocoding of crisis locations for map display
 * 
 * Features:
 * - Google Maps Geocoding API integration
 * - Error handling and fallbacks
 * - Coordinate validation
 * - Address parsing and normalization
 * - Reverse geocoding for human-readable location names
 */

import { doc, updateDoc } from 'firebase/firestore';

class CrisisGeocodingService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    this.serverEndpoint = process.env.REACT_APP_SERVER_URL || 'https://australia-southeast2-crisiscompass-prod.cloudfunctions.net';
  }

  /**
   * Reverse geocode coordinates to get human-readable location name
   * @param {number} latitude - The latitude coordinate
   * @param {number} longitude - The longitude coordinate
   * @returns {Promise<string|null>} - Human-readable location name or null if failed
   */
  async reverseGeocode(latitude, longitude) {
    try {
      console.log(`🔄 Reverse geocoding coordinates: ${latitude}, ${longitude}`);
      
      if (!this.apiKey) {
        console.warn('⚠️ Google Maps API key not available for reverse geocoding');
        return null;
      }

      // Validate coordinates
      if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
        console.warn('⚠️ Invalid coordinates for reverse geocoding');
        return null;
      }

      const reverseGeocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${this.apiKey}`;
      
      const response = await fetch(reverseGeocodeUrl);
      
      if (!response.ok) {
        console.warn(`⚠️ Reverse geocoding HTTP error: ${response.status}`);
        return null;
      }

      const data = await response.json();

      if (data.status !== 'OK' || !data.results || data.results.length === 0) {
        console.warn(`⚠️ Reverse geocoding failed: ${data.status}`);
        return null;
      }

      // Extract the full residential address from Google's response
      const result = data.results[0];
      let locationName = null;

      // 🏠 FULL ADDRESS: Use the formatted_address which includes complete street details
      // This gives us: "123 Main Street, Suburb, City, State, Postcode, Country"
      locationName = result.formatted_address;

      // Optional: Try to get a more specific street-level result if multiple results exist
      // Look for the most specific result (usually the first one with street_address type)
      for (const res of data.results) {
        if (res.types.includes('street_address') || res.types.includes('premise')) {
          locationName = res.formatted_address;
          console.log('🏠 Found street-level address:', locationName);
          break;
        }
      }

      console.log(`✅ Reverse geocoding successful: ${latitude}, ${longitude} -> ${locationName}`);
      return locationName;

    } catch (error) {
      console.warn('⚠️ Reverse geocoding error:', error.message);
      return null;
    }
  }

  /**
   * Geocode a crisis location automatically
   * @param {string} address - The address string to geocode
   * @param {string} incidentId - The incident ID to update (optional)
   * @param {object} db - Firestore database reference (for client-side updates)
   * @returns {Promise<object>} - { success, latitude, longitude, formattedAddress, reverseGeocodeName, error }
   */
  async geocodeCrisisLocation(address, incidentId = null, db = null) {
    console.log('🗺️ Starting crisis location geocoding for:', address);

    try {
      // Validate input
      if (!address || typeof address !== 'string' || address.trim().length < 3) {
        throw new Error('Invalid address provided for geocoding');
      }

      const cleanAddress = address.trim();
      
      // Method 1: Try backend geocoding service (more secure, includes server-side validation)
      try {
        const backendResult = await this.geocodeViaBackend(cleanAddress);
        if (backendResult.success) {
          console.log('✅ Backend geocoding successful:', backendResult);
          
          // Update incident if ID provided
          if (incidentId && db) {
            await this.updateIncidentCoordinates(incidentId, backendResult, db);
          }
          
          return backendResult;
        }
      } catch (backendError) {
        console.warn('⚠️ Backend geocoding failed, falling back to client-side:', backendError.message);
      }

      // Method 2: Fallback to client-side geocoding
      if (this.apiKey) {
        const clientResult = await this.geocodeViaClient(cleanAddress);
        if (clientResult.success) {
          console.log('✅ Client-side geocoding successful:', clientResult);
          
          // Update incident if ID provided
          if (incidentId && db) {
            await this.updateIncidentCoordinates(incidentId, clientResult, db);
          }
          
          return clientResult;
        }
      }

      // Both methods failed
      throw new Error('All geocoding methods failed');

    } catch (error) {
      console.error('❌ Crisis geocoding failed:', error);
      return {
        success: false,
        error: error.message || 'Unknown geocoding error',
        address: address
      };
    }
  }

  /**
   * Geocode via backend Cloud Function (preferred method)
   */
  async geocodeViaBackend(address) {
    const response = await fetch(`${this.serverEndpoint}/api/geocode-crisis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ address })
    });

    if (!response.ok) {
      throw new Error(`Backend geocoding HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Backend geocoding failed');
    }

    // 🔄 PHASE 1 ENHANCEMENT: Add reverse geocoding for human-readable location name
    let reverseGeocodeName = null;
    try {
      reverseGeocodeName = await this.reverseGeocode(result.latitude, result.longitude);
    } catch (reverseError) {
      console.warn('⚠️ Backend reverse geocoding failed:', reverseError.message);
      // Don't fail the main geocoding if reverse geocoding fails
    }

    return {
      success: true,
      latitude: result.latitude,
      longitude: result.longitude,
      formattedAddress: result.formattedAddress || address,
      reverseGeocodeName: reverseGeocodeName, // NEW: Human-readable location name
      accuracy: result.accuracy || 'unknown',
      source: 'backend'
    };
  }

  /**
   * Geocode via client-side Google Maps API (fallback)
   */
  async geocodeViaClient(address) {
    if (!this.apiKey) {
      throw new Error('Google Maps API key not configured');
    }

    // Add Australia bias to improve accuracy for Australian addresses
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&region=au&key=${this.apiKey}`;
    
    const response = await fetch(geocodeUrl);
    
    if (!response.ok) {
      throw new Error(`Google Maps API HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
      throw new Error(`Google Maps API error: ${data.status} - ${data.error_message || 'No results found'}`);
    }

    const result = data.results[0];
    const location = result.geometry.location;

    // Validate coordinates are reasonable (within global bounds)
    if (!this.validateCoordinates(location.lat, location.lng)) {
      throw new Error('Invalid coordinates returned from geocoding service');
    }

    // 🔄 PHASE 1 ENHANCEMENT: Add reverse geocoding for human-readable location name
    let reverseGeocodeName = null;
    try {
      reverseGeocodeName = await this.reverseGeocode(location.lat, location.lng);
    } catch (reverseError) {
      console.warn('⚠️ Client-side reverse geocoding failed:', reverseError.message);
      // Don't fail the main geocoding if reverse geocoding fails
    }

    return {
      success: true,
      latitude: location.lat,
      longitude: location.lng,
      formattedAddress: result.formatted_address,
      reverseGeocodeName: reverseGeocodeName, // NEW: Human-readable location name
      accuracy: result.geometry.location_type || 'unknown',
      source: 'client'
    };
  }

  /**
   * Update incident with geocoded coordinates
   */
  async updateIncidentCoordinates(incidentId, geocodingResult, db) {
    try {
      const incidentRef = doc(db, 'c3_incidents', incidentId);
      const updateData = {
        latitude: geocodingResult.latitude,
        longitude: geocodingResult.longitude,
        geocodedAt: new Date(),
        geocodingSource: geocodingResult.source || 'unknown'
      };

      if (geocodingResult.formattedAddress) {
        updateData.formattedLocation = geocodingResult.formattedAddress;
      }

      // 🔄 PHASE 1 ENHANCEMENT: Store reverse geocoded name
      if (geocodingResult.reverseGeocodeName) {
        updateData.reverseGeocodeName = geocodingResult.reverseGeocodeName;
      }

      await updateDoc(incidentRef, updateData);
      console.log('✅ Incident coordinates updated successfully:', incidentId);
    } catch (error) {
      console.error('❌ Failed to update incident coordinates:', error);
      throw error;
    }
  }

  /**
   * Validate coordinates are within reasonable bounds
   */
  validateCoordinates(lat, lng) {
    return (
      typeof lat === 'number' && 
      typeof lng === 'number' &&
      lat >= -90 && lat <= 90 &&
      lng >= -180 && lng <= 180 &&
      !isNaN(lat) && !isNaN(lng)
    );
  }

  /**
   * Check if an address needs geocoding (has no coordinates)
   */
  needsGeocoding(incident) {
    return (
      incident?.location && 
      typeof incident.location === 'string' && 
      incident.location.trim().length > 0 &&
      (!incident.latitude || !incident.longitude || 
       isNaN(incident.latitude) || isNaN(incident.longitude))
    );
  }

  /**
   * Get display status for crisis location
   */
  getLocationStatus(incident) {
    if (!incident?.location) {
      return { status: 'no_location', message: 'No location specified' };
    }

    if (incident.latitude && incident.longitude && 
        !isNaN(incident.latitude) && !isNaN(incident.longitude)) {
      return { 
        status: 'geocoded', 
        message: 'Location mapped successfully',
        coordinates: { lat: incident.latitude, lng: incident.longitude }
      };
    }

    return { 
      status: 'needs_geocoding', 
      message: 'Location needs to be geocoded for map display',
      address: incident.location
    };
  }
}

// Create singleton instance
const geocodingService = new CrisisGeocodingService();

export default geocodingService;
export { CrisisGeocodingService }; 